import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import React, { useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { ImageModal } from '@/components/markdown/ImageModal';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { useDownloadFile, useGetFile, useMy } from '@/hooks';
import { useGlobalState } from '@/state/global.state';
import { Operator } from '@/types';

import {
  buildWebpageTree,
  collectFolderPaths,
} from './sitemap/buildWebpageTree';
import { SitemapActions } from './sitemap/SitemapActions';
import { SitemapFilterChips } from './sitemap/SitemapFilterChips';
import { SitemapTree } from './sitemap/SitemapTree';
import {
  SignalKey,
  SitemapRowHandlers,
  WebpageTreeNode,
  WebpageWithRisk,
} from './sitemap/types';
import { matchesAllSignals } from './sitemap/webpageSignals';

interface WebApplicationSitemapProps {
  asset: {
    key: string;
    identifier: string;
    details_path?: string;
    detailsPath?: string;
  };
}

interface WebApplicationDetailsBlob {
  openapi?: string;
}

const extractOpenAPI = (
  details: WebApplicationDetailsBlob | string | null | undefined
): string | undefined => {
  if (!details) return undefined;

  let parsed: WebApplicationDetailsBlob | null = null;
  if (typeof details === 'string') {
    try {
      parsed = JSON.parse(details) as WebApplicationDetailsBlob;
    } catch {
      parsed = null;
    }
  } else if (typeof details === 'object') {
    parsed = details;
  }

  const openAPI = parsed?.openapi;
  if (!openAPI || !openAPI.trim()) {
    return undefined;
  }

  return openAPI;
};

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return 'Unknown';
  }
};

const WebApplicationSitemap: React.FC<WebApplicationSitemapProps> = ({
  asset,
}) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  // Client-side sitemap state.
  const [activeFilters, setActiveFilters] = useState<Set<SignalKey>>(
    () => new Set()
  );
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  const {
    data: webpageTree,
    status: webpagesStatus,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMy(
    {
      resource: 'webpage',
      tree: true,
      query: {
        limit: 100,
        orderBy: 'url',
        descending: false,
        node: {
          labels: ['Webpage'],
          filters: [
            ...(debouncedSearch
              ? [
                  {
                    field: 'url',
                    operator: 'CONTAINS' as Operator,
                    value: debouncedSearch,
                  },
                ]
              : []),
          ],
          relationships: [
            {
              label: 'HAS_WEBPAGE',
              source: {
                labels: ['Asset'],
                filters: [
                  {
                    field: 'key',
                    operator: '=',
                    value: asset.key,
                  },
                ],
              },
            },
            {
              optional: true,
              label: 'HAS_VULNERABILITY',
              select_one: true,
              target: {
                labels: ['Risk'],
              },
            },
          ],
        },
      },
    },
    { enabled: !!asset?.key }
  );

  const webpages = useMemo(() => {
    // Each tree node's `result.webpages` can hold MORE THAN ONE webpage (the
    // backend GraphSearch result is a collection). Take them ALL — indexing
    // `[0]` silently dropped the rest, hiding pages (e.g. query-string variants
    // like /Account/Login?ReturnUrl=%2F) that shared a node.
    return (webpageTree ?? []).flatMap(node => {
      const nodeWebpages = (node?.node?.result?.webpages ??
        []) as WebpageWithRisk[];
      const risk = node?.node?.relationships?.[1]?.target?.result?.risks?.[0];
      // The HAS_VULNERABILITY relationship belongs to the node's matched (first)
      // webpage. Stamping it onto EVERY webpage in a multi-webpage node would
      // show one page's secret risk on unrelated pages and open the wrong
      // vulnerability when clicked, so only the first webpage carries the risk.
      return nodeWebpages.map((webpage, index) => ({
        ...webpage,
        risk: index === 0 ? risk : undefined,
      }));
    }) as WebpageWithRisk[];
  }, [webpageTree]);

  const { mutateAsync: downloadFile } = useDownloadFile();
  const { drawerState } = useGlobalState();
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  const detailsFilepath = useMemo(() => {
    const rawPath = asset.details_path ?? asset.detailsPath;
    if (!rawPath) return '';

    const normalized = rawPath.trim();
    if (
      !normalized ||
      normalized.toLowerCase() === 'null' ||
      normalized.toLowerCase() === 'undefined'
    ) {
      return '';
    }

    return normalized;
  }, [asset.details_path, asset.detailsPath]);

  const { data: webAppDetails, isFetching: isFetchingWebAppDetails } =
    useGetFile<WebApplicationDetailsBlob | string>(
      { name: detailsFilepath },
      {
        enabled: Boolean(detailsFilepath),
        retry: 0,
      }
    );

  const imageSrcs = useMemo(() => {
    return (webpages || [])
      .map(wp => wp.screenshot)
      .filter(Boolean) as string[];
  }, [webpages]);

  const primaryHostname = useMemo(() => {
    return getHostname(asset.identifier);
  }, [asset.identifier]);

  const openAPI = useMemo(() => extractOpenAPI(webAppDetails), [webAppDetails]);

  const handleDownloadOpenAPI = () => {
    if (!openAPI) return;

    const blob = new Blob([openAPI], {
      type: 'application/yaml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${primaryHostname || 'web-application'}-openapi.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const { internalWebpages, externalWebpages } = useMemo(() => {
    const internal: WebpageWithRisk[] = [];
    const external: WebpageWithRisk[] = [];

    for (const wp of webpages) {
      const hostname = getHostname(wp.url);
      if (hostname === primaryHostname) {
        internal.push(wp);
      } else {
        external.push(wp);
      }
    }

    return { internalWebpages: internal, externalWebpages: external };
  }, [webpages, primaryHostname]);

  // Apply the active signal filters (AND) before building the tree, so empty
  // folders drop out automatically.
  const filteredInternal = useMemo(
    () => internalWebpages.filter(wp => matchesAllSignals(wp, activeFilters)),
    [internalWebpages, activeFilters]
  );
  const filteredExternal = useMemo(
    () => externalWebpages.filter(wp => matchesAllSignals(wp, activeFilters)),
    [externalWebpages, activeFilters]
  );

  const internalTree = useMemo(
    () => buildWebpageTree(filteredInternal),
    [filteredInternal]
  );
  const externalNodes = useMemo<WebpageTreeNode[]>(
    () =>
      filteredExternal.map(wp => ({
        name: wp.url,
        path: wp.key,
        webpage: wp,
      })),
    [filteredExternal]
  );
  const allFolderPaths = useMemo(
    () => collectFolderPaths(internalTree),
    [internalTree]
  );

  const hasActiveFilters = activeFilters.size > 0;

  const toggleFolder = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };
  const showAll = () => setExpanded(new Set(allFolderPaths));
  const collapseAll = () => setExpanded(new Set());

  const toggleFilter = (key: SignalKey) => {
    const next = new Set(activeFilters);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setActiveFilters(next);
    // Reveal matches by expanding every folder of the newly-filtered tree, but
    // keep this in `expanded` STATE (not a render-time override) so folders
    // stay collapsible and "Collapse all" still works while filters are active.
    if (next.size > 0) {
      const filtered = internalWebpages.filter(wp =>
        matchesAllSignals(wp, next)
      );
      setExpanded(new Set(collectFolderPaths(buildWebpageTree(filtered))));
    }
  };
  const clearFilters = () => setActiveFilters(new Set());

  const handlers: SitemapRowHandlers = {
    onOpenVulnerability: wp => {
      if (wp.risk?.key) {
        drawerState.handleOpenVulnerabilityDrawer(wp.risk);
      }
    },
    onOpenScreenshot: wp => {
      if (!wp.screenshot) return;
      const index = imageSrcs.indexOf(wp.screenshot);
      if (index === -1) return;
      setImageModalOpen(true);
      setImageIndex(index);
    },
    onDownloadResponse: wp => {
      downloadFile({
        name: wp.details_filepath,
        downloadName: `${asset.identifier}-webpage-${wp.key.split('#').pop()}.html`,
      });
    },
    onOpenUrl: wp => {
      window.open(wp.url, '_blank', 'noopener,noreferrer');
    },
  };

  return (
    <div className="flex h-full flex-col gap-4">
      {detailsFilepath && openAPI && (
        <div className="flex justify-end">
          <button
            type="button"
            className="border-chariot-brand-primary bg-chariot-background-secondary text-chariot-brand-primary inline-flex h-fit cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium"
            onClick={handleDownloadOpenAPI}
            disabled={isFetchingWebAppDetails}
          >
            <ArrowDownTrayIcon className="size-4" />
            Download OpenAPI YAML
          </button>
        </div>
      )}

      <TableControlsContainer
        className="mb-0"
        filterLabel
        data-testid="sitemap-controls"
        filters={
          <SitemapFilterChips
            active={activeFilters}
            onToggle={toggleFilter}
            onClear={clearFilters}
          />
        }
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64 shrink-0 p-1 pb-0"
            value={search}
            onChange={setSearch}
            placeholder="Search"
            disabled={webpagesStatus === 'pending'}
          />
        }
        selectionBar={
          <SitemapActions onShowAll={showAll} onCollapseAll={collapseAll} />
        }
      />

      <SitemapTree
        internalNodes={internalTree}
        externalNodes={externalNodes}
        expanded={expanded}
        onToggle={toggleFolder}
        handlers={handlers}
        status={webpagesStatus}
        internalCount={filteredInternal.length}
        externalCount={filteredExternal.length}
        itemCount={webpages.length}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage === true}
        isFetchingNextPage={isFetchingNextPage}
        hasActiveFilters={hasActiveFilters}
        hasActiveSearch={debouncedSearch.trim().length > 0}
      />

      <ImageModal
        open={imageModalOpen}
        setOpen={setImageModalOpen}
        imageSrcs={imageSrcs}
        imageIndex={imageIndex}
        setImageIndex={setImageIndex}
      />
    </div>
  );
};

export default WebApplicationSitemap;
